/**
 * Shared types and formatting for the spend surface (/admin/usage).
 * Mirrors the backend contract of GET /admin/usage and GET /admin/usage/:id.
 */

export interface UsageTotals {
  totalCost: number;
  totalTokens: number;
  inputTokens: number;
  outputTokens: number;
  operations: number;
  failedOperations: number;
  accounts: number;
  firstActivityAt: string | null;
  lastActivityAt: string | null;
}

export interface UsageKindBreakdownItem {
  kind: string;
  totalCost: number;
  totalTokens: number;
  operations: number;
}

export interface UsageDailyPoint {
  day: string;
  totalCost: number;
  totalTokens: number;
  operations: number;
}

export interface UsageAccountRow {
  accountId: string;
  email: string | null;
  totalCost: number;
  totalTokens: number;
  operations: number;
  lastActivityAt: string | null;
}

export interface UsageWindowInfo {
  from: string | null;
  to: string | null;
}

export interface UsageOverview {
  period: UsageTotals;
  allTime: UsageTotals;
  window: UsageWindowInfo;
  byKind: UsageKindBreakdownItem[];
  daily: UsageDailyPoint[];
  items: UsageAccountRow[];
}

export interface AccountUsage {
  accountId: string;
  period: UsageTotals;
  allTime: UsageTotals;
  window: UsageWindowInfo;
  byKind: UsageKindBreakdownItem[];
  daily: UsageDailyPoint[];
}

export const USAGE_KIND_LABELS: Record<string, string> = {
  ai_chat: "Чат",
  ai_plan: "Генерация плана",
  import: "Импорт",
  place_resolution: "Разрешение мест",
};

export function usageKindLabel(kind: string): string {
  return USAGE_KIND_LABELS[kind] ?? kind;
}

/**
 * Spend is sub-cent per operation, so a plain currency format would round most
 * rows to $0.00. Small amounts keep four decimals; larger ones keep two.
 */
export function formatCost(value: number): string {
  if (!Number.isFinite(value)) return "$0.00";
  if (value === 0) return "$0.00";
  if (Math.abs(value) < 1) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
}

export function formatCount(value: number): string {
  if (!Number.isFinite(value)) return "0";
  return new Intl.NumberFormat("ru-RU").format(Math.round(value));
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
    })
      .format(new Date(iso))
      .replace(",", "");
  } catch {
    return iso;
  }
}

export function formatDay(day: string): string {
  try {
    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      timeZone: "UTC",
    }).format(new Date(`${day}T00:00:00.000Z`));
  } catch {
    return day;
  }
}

export interface UsagePeriodState {
  /** YYYY-MM-DD, empty string means "unbounded". */
  from: string;
  to: string;
}

export const EMPTY_USAGE_PERIOD: UsagePeriodState = { from: "", to: "" };

/** Last `days` calendar days including today, in UTC. */
export function lastDaysPeriod(days: number, now: Date = new Date()): UsagePeriodState {
  const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const from = new Date(to.getTime() - (days - 1) * 24 * 60 * 60 * 1000);
  return { from: toDateInput(from), to: toDateInput(to) };
}

export function currentMonthPeriod(now: Date = new Date()): UsagePeriodState {
  const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  return { from: toDateInput(first), to: toDateInput(today) };
}

function toDateInput(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Builds the `?from=&to=` suffix; omits empty bounds so the API sees all-time. */
export function usagePeriodQuery(period: UsagePeriodState): string {
  const params = new URLSearchParams();
  if (period.from) params.set("from", period.from);
  if (period.to) params.set("to", period.to);
  const query = params.toString();
  return query ? `?${query}` : "";
}

/** Human label for the active window, used above the period figures. */
export function describePeriod(period: UsagePeriodState): string {
  if (!period.from && !period.to) return "за всё время";
  if (period.from && period.to) return `${formatDay(period.from)} — ${formatDay(period.to)}`;
  if (period.from) return `с ${formatDay(period.from)}`;
  return `по ${formatDay(period.to)}`;
}
